import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Package, Truck, Box, Building, DollarSign, RotateCcw, Search, Rocket, Printer, ArrowLeftRight, BarChart3, CreditCard, TrendingUp, Smartphone, User, ShoppingCart, MapPin, Bike, Battery, Ruler, Calculator } from 'lucide-react';
import LocationSearch from '../components/ui/LocationSearch';
import { expeditionAPI, carrierAPI, relayAPI } from '../services/api';
import { calculateTariff, CITY_ZONE_MAPPING } from '../services/tariffService';
import ErrorModal from '../components/ErrorModal';
import { TrackingTimelineBlock } from '../components/TrackingTimelineBlock';
import { cn } from '../utils/cn';

// Types de véhicules avec spécifications poids et volume exactes
const VEHICLE_TYPES = [
  { 
    id: 2, 
    name: 'Moto', 
    icon: '🏍️',
    min_weight: 0,
    max_weight: 51, 
    min_volume: 0,
    max_volume: 0.160,
    description: 'Moto - Poids < 51kg, Volume < 0.16m³' 
  },
  { 
    id: 1, 
    name: 'Voiture/Van/Tricycle', 
    icon: '🚗',
    min_weight: 51,
    max_weight: 201, 
    min_volume: 0.16,
    max_volume: 0.54,
    description: 'Voiture/Van/Tricycle - Poids 51-201kg, Volume 0.16-0.54m³' 
  },
  { 
    id: 4, 
    name: 'Fourgon', 
    icon: '🚐',
    min_weight: 201,
    max_weight: 2001, 
    min_volume: 0.54,
    max_volume: 19.2,
    description: 'Fourgon - Poids 201-2000kg, Volume 0.54-19.2m³' 
  },
  { 
    id: 7, 
    name: 'Camion', 
    icon: '🚛',
    min_weight: 2001,
    max_weight: 40000, 
    min_volume: 19.2,
    max_volume: 999,
    description: 'Camion - Poids > 2001kg, Volume > 19.2m³' 
  }
];

// 🏙️ Listes des villes et communes (pour usage futur)
// 🏙️ Listes des villes et communes
const ABIDJAN_COMMUNES = [
  'Abobo', 'Adjamé', 'Attecoubé', 'Cocody', 'Koumassi', 
  'Marcory', 'Plateau', 'Port-Bouët', 'Treichville', 'Yopougon'
];

// 🏙️ Communes d'Abidjan à exclure des villes interurbaines
const ABIDJAN_COMMUNES_TO_EXCLUDE = [
  'Abobo', 'Adjamé', 'Attecoubé', 'Cocody', 'Koumassi', 
  'Marcory', 'Plateau', 'Port-Bouët', 'Treichville', 'Yopougon'
];

// 🏙️ Toutes les villes (incluant les communes d'Abidjan)
const ALL_CITIES_WITH_COMMUNES = Object.keys(CITY_ZONE_MAPPING).sort();

// 🏙️ Villes interurbaines (excluant les communes d'Abidjan)
const ALL_CITIES = ALL_CITIES_WITH_COMMUNES.filter(city => 
  !ABIDJAN_COMMUNES_TO_EXCLUDE.includes(city)
);

const initialAddress = {
  name: 'Non renseigné',
  phone: 'Non renseigné',
  email: 'Non renseigné',
  address: 'Non renseigné',
  company: ''
};

const STATUS_LABELS = {
  '1': 'Planifié',
  '2': 'En cours d\'affectation',
  '3': 'En chemin vers le lieu de ramassage',
  '4': 'Arrivé au lieu de ramassage',
  '5': 'En chemin vers la destination',
  '6': 'Arrivé à destination',
  '7': 'Retour au magasin',
  '8': 'Terminé',
  '9': 'Livraison annulée',
};
const formatDate = (date, options = {}) => {
  if (!date) return '—';
  try {
    return new Date(date).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...options,
    });
  } catch (error) {
    return date;
  }
};

// Libellés des événements de traçabilité (suivi public)
const EVENT_TYPE_LABELS = {
  CREATION: 'Commande acceptée',
  DEPART: 'Colis expédié',
  RECEPTION: 'Colis en transit',
};
const getEventLabel = (event) => {
  const type = event?.event_type || '';
  return EVENT_TYPE_LABELS[type] || event?.event_type_display || (type && type.replace(/_/g, ' ')) || '—';
};

const extractAddressInfo = (addressObj) => {
  if (!addressObj) return { name: '—', phone: '—', email: '—', address: '—', company: '' };

  if (typeof addressObj === 'string') {
    return {
      name: addressObj,
      phone: 'Non renseigné',
      email: 'Non renseigné',
      address: addressObj,
      company: ''
    };
  }

  if (typeof addressObj === 'object') {
    return {
      name: `${addressObj.customer_first_name || ''} ${addressObj.customer_last_name || ''}`.trim() ||
        `${addressObj.recipient_first_name || ''} ${addressObj.recipient_last_name || ''}`.trim() ||
        addressObj.name || 'Non renseigné',
      phone: addressObj.customer_phone_number || addressObj.pickup_phone_number || 'Non renseigné',
      email: addressObj.customer_email || addressObj.recipient_email || addressObj.pickup_email || addressObj.email || 'Non renseigné',
      address: addressObj.address || addressObj.full_address || 'Non renseigné',
      company: addressObj.pickup_company_name || addressObj.company_name || ''
    };
  }

  return initialAddress;
};

export default function Home() {
  const navigate = useNavigate();
  const [trackingNumber, setTrackingNumber] = useState('');
  const [isTrackingLoading, setIsTrackingLoading] = useState(false);
  const [trackingError, setTrackingError] = useState('');
  const [trackingResult, setTrackingResult] = useState(null);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  
  const senderInfo = trackingResult ? extractAddressInfo(trackingResult.adresse_expediteur) : initialAddress;
  const recipientInfo = trackingResult ? extractAddressInfo(trackingResult.adresse_destinataire) : initialAddress;
  const parcels = trackingResult?.infocolis || [];
  const statusExp = trackingResult?.statut_exp || '';
  const statusRecep = trackingResult?.statut_recep || '';
  const globalStatus = trackingResult?.statut_colis || '';
  const statusCode = normalizeStatusCode(globalStatus);
  const isReturnStatus = statusCode === '7';
  const isCancelledStatus = statusCode === '9';
  const distanceKm = trackingResult?.dataApi?.merged_est_dist_km || '--';
  const durationMinutes = trackingResult?.dataApi?.merged_est_minutes || '--';
  const deliveryEta = trackingResult?.delais_livraison || '--';
  const modeExpedition = trackingResult?.mode_expedition || '';
  
  // Déterminer si on affiche l'étape "Arrivée à destination"
  const isRelayMode = modeExpedition?.toLowerCase().includes('Point relais') || modeExpedition?.toLowerCase().includes('relais');
  const isDomicileMode = modeExpedition?.toLowerCase().includes('Livraison à domicile');
  
  // Pour le mode Point Relais, on utilise statut_exp et statut_recep
  // statut_exp: "à expédier" → Commande créée + Ramassage
  //             "en transit" → En transit
  //             "expédié" → Livré
  // statut_recep: "receptionné" → Arrivé à destination
  //               "livré" → Livré (alternative)

  // Calcul des étapes selon le mode d'expédition
  let step1Completed, step2Completed, step3Completed, step3InTransit, step3InProgress;
  
  if (isRelayMode) {
    // Mode Point Relais : Utiliser statut_exp et statut_recep
    const statusExpLower = statusExp?.toLowerCase().trim() || '';
    const statusRecepLower = statusRecep?.toLowerCase().trim() || '';
    
    // Étape 1 (Commandé) : Complétée si statut_exp existe (à expédier, en transit, ou expédié)
    step1Completed = ['à expédier', 'a expédier', 'a expedier', 'à expedier', 'en transit', 'expédié', 'expedie'].includes(statusExpLower);
    
    // Étape 2 (Ramassage) : Complétée si "à expédier" ou plus avancé
    step2Completed = ['à expédier', 'a expédier', 'a expedier', 'à expedier', 'en transit', 'expédié', 'expedie'].includes(statusExpLower);
    
    // Étape 3 (En transit) : Complétée si "en transit" ou "expédié" ou "receptionné"
    step3Completed = ['expédié', 'expedie'].includes(statusExpLower) || statusRecepLower === 'receptionné';
    step3InTransit = statusExpLower === 'en transit';
    step3InProgress = !step3Completed && step3InTransit;
  } else {
    // Mode Domicile : Utiliser statut_colis (logique existante)
    step1Completed = [
      'Planifié',
      "En cours d'affectation",
      'En chemin vers le lieu de ramassage',
      'En chemin vers la destination',
      'En Chemin vers la destination',
      'En  chemin vers la destination',
      'Arrivé à destination',
      'Terminé',
      'Retour au magasin',
      'Livraison annulée',
    ].includes(globalStatus);

    step2Completed = [
      'En chemin vers la destination',
      'En Chemin vers la destination',
      'En  chemin vers la destination',
      'Arrivé à destination',
      'Terminé',
      'Retour au magasin',
      'Livraison annulée',
    ].includes(globalStatus);

    step3Completed = [
      'Arrivé à destination',
      'Terminé',
      'Retour au magasin',
      'Livraison annulée',
    ].includes(globalStatus);

    step3InTransit = [
      'En chemin vers la destination',
      'En Chemin vers la destination',
      'En  chemin vers la destination',
    ].includes(globalStatus);

    step3InProgress = !step3Completed && step3InTransit;
  }

  // Logique adaptée selon le mode d'expédition
  let step4Reached, step4Completed, step4InProgress, step5Completed, step5InProgress;
  let totalSteps;
  let step2InProgress;
  
  if (isRelayMode) {
    // Mode Point Relais : Utiliser statut_exp et statut_recep
    const statusExpLower = statusExp?.toLowerCase().trim() || '';
    const statusRecepLower = statusRecep?.toLowerCase().trim() || '';
    
    // Vérifier si livré : statut_exp === "expédié" ET statut_recep === "livré"
    const isDelivered = (statusExpLower === 'expédié' || statusExpLower === 'expedie') && 
                        (statusRecepLower === 'livré' || statusRecepLower === 'livre');
    
    if (isDelivered) {
      // Une fois livré (statut_exp = "expédié"), "Livraison" remplace "Arrivée à destination" (étape 4)
      step4Reached = true;
      step4Completed = true;
      step4InProgress = false;
      step5Completed = false;
      step5InProgress = false;
      totalSteps = 4; // Timeline à 4 étapes
    } else {
      // Avant livraison, on affiche "Arrivée à destination" si statut_recep = "receptionné"
      step4Reached = statusRecepLower === 'receptionné';
      step4Completed = false;
      step4InProgress = step4Reached;
      step5Completed = false;
      step5InProgress = false;
      totalSteps = 4; // Timeline à 4 étapes (pas de step5)
    }
    
    // Étape 2 en cours si statut_exp = "à expédier" mais pas encore en transit
    step2InProgress = ['à expédier', 'a expédier', 'a expedier', 'à expedier'].includes(statusExpLower) && !step3InTransit;
  } else {
    // Mode Domicile : Utiliser statut_colis (logique existante)
    step4Reached = false;
    step4Completed = false;
    step4InProgress = false;
    step5Completed = statusRecep === 'livré' && statusCode === '8';
    step5InProgress = !step5Completed && (statusCode === '8' || isReturnStatus || isCancelledStatus);
    totalSteps = 4; // Timeline à 4 étapes
    
    step2InProgress = !step2Completed && globalStatus === 'Arrivé au lieu de ramassage';
  }

  // Calcul de progression adapté selon le mode
  let completedSteps, progressIndexValue;
  
  if (isRelayMode) {
    const statusExpLower = statusExp?.toLowerCase().trim() || '';
    const statusRecepLower = statusRecep?.toLowerCase().trim() || '';
    const isDelivered = (statusExpLower === 'expédié' || statusExpLower === 'expedie') || 
                        (statusRecepLower === 'livré' || statusRecepLower === 'livre');
    
    if (isDelivered) {
      // Mode relais livré : 4 étapes avec la 4ème = Livraison
      completedSteps = [step1Completed, step2Completed, step3Completed, true].filter(Boolean).length;
      progressIndexValue = completedSteps;
    } else {
      // Mode relais avant livraison : 4 étapes avec la 4ème = Arrivée
      completedSteps = [step1Completed, step2Completed, step3Completed, step4Completed].filter(Boolean).length;
      progressIndexValue = completedSteps;
      if (step4InProgress) progressIndexValue = Math.max(progressIndexValue, 3.5);
    }
  } else {
    // Mode domicile : 4 étapes (1. Commandé, 2. Ramassage, 3. Transit, 4. Livraison)
    completedSteps = [step1Completed, step2Completed, step3Completed, step5Completed].filter(Boolean).length;
    progressIndexValue = completedSteps;
    if (step5InProgress) progressIndexValue = Math.max(progressIndexValue, 3.5);
  }
  
  if (!step1Completed) progressIndexValue = Math.max(progressIndexValue, 0.5);
  if (step2InProgress) progressIndexValue = Math.max(progressIndexValue, 1.5);
  if (step3InProgress) progressIndexValue = Math.max(progressIndexValue, 2.5);

  const completedSegments = Math.max(0, progressIndexValue - 1);
  const progressLineFill = totalSteps > 1
    ? Math.min(100, Math.max(0, (completedSegments / (totalSteps - 1)) * 100))
    : 0;

  const deliveryStepTitle = isCancelledStatus ? 'Annulation' : isReturnStatus ? 'Retour' : 'Livraison';
  const deliveryStepVariant = isCancelledStatus ? 'cancelled' : isReturnStatus ? 'return' : 'default';

  // Timeline adaptée selon le mode d'expédition
  let stepsTimeline;
  
  if (isRelayMode) {
    const statusExpLower = statusExp?.toLowerCase().trim() || '';
    const statusRecepLower = statusRecep?.toLowerCase().trim() || '';
    const isDelivered = (statusExpLower === 'expédié' || statusExpLower === 'expedie') || 
                        (statusRecepLower === 'livré' || statusRecepLower === 'livre');
    
    if (isDelivered) {
      // Mode relais livré : La dernière étape est "Livraison"
      stepsTimeline = [
        { key: 'ordered', title: '', Icon: Box, completed: step1Completed, inProgress: !step1Completed, variant: 'default' },
        { key: 'pickup', title: '', Icon: MapPin, completed: step2Completed, inProgress: step2InProgress, variant: 'default' },
        { key: 'transit', title: '', Icon: Truck, completed: step3Completed, inProgress: step3InProgress, variant: 'default' },
        { key: 'delivery', title: deliveryStepTitle, Icon: Package, completed: true, inProgress: false, variant: deliveryStepVariant },
      ];
    } else {
      // Mode relais avant livraison : La dernière étape est "Arrivée à destination"
      stepsTimeline = [
        { key: 'ordered', title: '', Icon: Box, completed: step1Completed, inProgress: !step1Completed, variant: 'default' },
        { key: 'pickup', title: '', Icon: MapPin, completed: step2Completed, inProgress: step2InProgress, variant: 'default' },
        { key: 'transit', title: '', Icon: Truck, completed: step3Completed, inProgress: step3InProgress, variant: 'default' },
        { key: 'arrival', title: '', Icon: MapPin, completed: step4Completed || step4InProgress, inProgress: step4InProgress, variant: 'arrival' },
      ];
    }
  } else {
    // Mode domicile : 4 étapes sans "Arrivée à destination"
    stepsTimeline = [
      { key: 'ordered', title: '', Icon: Box, completed: step1Completed, inProgress: !step1Completed, variant: 'default' },
      { key: 'pickup', title: '', Icon: MapPin, completed: step2Completed, inProgress: step2InProgress, variant: 'default' },
      { key: 'transit', title: '', Icon: Truck, completed: step3Completed, inProgress: step3InProgress, variant: 'default' },
      { key: 'delivery', title: deliveryStepTitle, Icon: Package, completed: step5Completed, inProgress: step5InProgress, variant: deliveryStepVariant },
    ];
  }

  const arrivalCardClasses = step4Completed || step4InProgress ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200';
  const arrivalBadgeClasses = step4Completed
    ? 'bg-blue-100 text-blue-800'
    : step4InProgress
    ? 'bg-blue-100 text-blue-800'
    : 'bg-gray-100 text-gray-500';
  const arrivalBadgeLabel = step4Completed
    ? '✅ Arrivé'
    : step4InProgress
    ? '📍 Arrivé'
    : '⏳ En attente';
  const arrivalMessageClass = step4Completed || step4InProgress ? 'text-blue-700' : 'text-gray-600';
  const arrivalMessageText = step4Completed || step4InProgress
    ? "Le colis est bien arrivé à destination."
    : "Le colis sera marqué arrivé dès qu'il atteindra la destination.";
  const arrivalInfoBackground = step4Completed || step4InProgress ? 'bg-blue-50' : 'bg-gray-100';
  const arrivalInfoHeaderClass = step4Completed || step4InProgress ? 'text-blue-900' : 'text-gray-900';
  const arrivalInfoTextClass = step4Completed || step4InProgress ? 'text-blue-800' : 'text-gray-700';
  const arrivalInfoLabelClass = step4Completed || step4InProgress ? 'text-blue-700' : 'text-gray-600';

  const deliveryBadgeClasses = step5Completed
    ? 'bg-green-100 text-green-800'
    : isCancelledStatus
    ? 'bg-red-100 text-red-700'
    : isReturnStatus
    ? 'bg-purple-100 text-purple-700'
    : 'bg-gray-100 text-gray-500';
  const deliveryBadgeLabel = step5Completed
    ? '✅ Terminé'
    : isCancelledStatus
    ? '❌ Annulé'
    : isReturnStatus
    ? '↩️ Retour'
    : '⏳ En attente';
  const deliveryCardClasses = step5Completed
    ? 'bg-white border-green-200'
    : isCancelledStatus
    ? 'bg-red-50 border-red-200'
    : isReturnStatus
    ? 'bg-purple-50 border-purple-200'
    : 'bg-gray-50 border-gray-200';
  const deliveryMessageClass = step5Completed
    ? 'text-green-700'
    : isCancelledStatus
    ? 'text-red-600'
    : isReturnStatus
    ? 'text-purple-600'
    : 'text-gray-600';
  const deliveryMessageText = step5Completed
    ? "Votre colis a été livré avec succès à l'adresse de destination."
    : isCancelledStatus
    ? 'La livraison a été annulée. Veuillez nous contacter pour définir les prochaines étapes.'
    : isReturnStatus
    ? 'Le colis est retourné au magasin. Contactez-nous pour programmer un nouvel envoi ou un retrait.'
    : 'Le colis sera livré une fois le transit terminé.';
  const deliveryInfoBackground = step5Completed
    ? 'bg-green-50'
    : isCancelledStatus
    ? 'bg-red-50'
    : isReturnStatus
    ? 'bg-purple-50'
    : 'bg-green-50';
  const deliveryInfoHeaderClass = step5Completed
    ? 'text-green-900'
    : isCancelledStatus
    ? 'text-red-900'
    : isReturnStatus
    ? 'text-purple-900'
    : 'text-green-900';
  const deliveryInfoTextClass = step5Completed
    ? 'text-green-800'
    : isCancelledStatus
    ? 'text-red-800'
    : isReturnStatus
    ? 'text-purple-800'
    : 'text-green-800';
  const deliveryInfoLabelClass = step5Completed
    ? 'text-green-700'
    : isCancelledStatus
    ? 'text-red-700'
    : isReturnStatus
    ? 'text-purple-700'
    : 'text-green-700';


  // Estimation states
  const [pickupLocation, setPickupLocation] = useState('');
  const [deliveryLocation, setDeliveryLocation] = useState('');
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [declaredValue, setDeclaredValue] = useState('');
  const [deliveryMode, setDeliveryMode] = useState('domicile');
  const [serviceType, setServiceType] = useState('');
  const [isEstimationLoading, setIsEstimationLoading] = useState(false);
  const [estimationResult, setEstimationResult] = useState(null);
  const [carrierName, setCarrierName] = useState('');
  const [relayName, setRelayName] = useState('');
  const [showSimulation, setShowSimulation] = useState(false);
  const [showEstimationForm, setShowEstimationForm] = useState(false);
  const [showEstimationModal, setShowEstimationModal] = useState(false);
  
  // Nouveaux états pour la sélection des villes et types de service
  const [departureCity, setDepartureCity] = useState('');
  const [destinationCity, setDestinationCity] = useState('');
  const [selectedVehicleType, setSelectedVehicleType] = useState(2); // Moto par défaut
  
  // États pour les formats de colis
  const [selectedPackageFormat, setSelectedPackageFormat] = useState('');
  const [showCustomDimensions, setShowCustomDimensions] = useState(false);
  
  // États pour le modal d'erreur
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalData, setErrorModalData] = useState({ title: '', message: '', type: 'error' });
  
  // États pour les points relais et transporteur du gérant
  const [selectedRelayPoint] = useState(null); // TODO: Implémenter la sélection de point relais
  // const [relayPoints, setRelayPoints] = useState([]);
  // const [isLoadingRelayPoints, setIsLoadingRelayPoints] = useState(false);

  // Formats de colis standardisés (NOUVEAUX - 2024)
  const packageFormats = [
    {
      id: 'xs',
      name: 'XS - Petit Colis',
      volume: 5000, // cm³
      weight: 2, // kg
      dimensions: { length: 25, width: 20, height: 10 },
      examples: 'Documents, accessoires, petits appareils',
      description: 'Volume max: ~5000 cm³ (25 x 20 x 10 cm) - Poids max: 2 kg'
    },
    {
      id: 's',
      name: 'S - Colis Moyen',
      volume: 20000, // cm³
      weight: 5, // kg
      dimensions: { length: 40, width: 25, height: 20 },
      examples: 'Vêtements, petite électronique, articles ménagers',
      description: 'Volume max: ~20000 cm³ (40 x 25 x 20 cm) - Poids max: 5 kg'
    },
    {
      id: 'm',
      name: 'M - Colis Standard',
      volume: 50000, // cm³
      weight: 15, // kg
      dimensions: { length: 50, width: 30, height: 30 },
      examples: 'Électroménager compact, produits alimentaires',
      description: 'Volume max: ~50000 cm³ (50 x 30 x 30 cm) - Poids max: 15 kg'
    },
    {
      id: 'l',
      name: 'L - Grand Colis',
      volume: 120000, // cm³
      weight: 25, // kg
      dimensions: { length: 60, width: 40, height: 50 },
      examples: 'Matériel professionnel, gros équipements',
      description: 'Volume max: ~120000 cm³ (60 x 40 x 50 cm) - Poids max: 25 kg'
    },
    {
      id: 'xl',
      name: 'XL - Colis Spécial / Hors Gabarit',
      volume: 0, // cm³ - Saisie manuelle
      weight: 0, // kg - Saisie manuelle
      dimensions: { length: 0, width: 0, height: 0 }, // Saisie manuelle
      examples: 'Colis spécial nécessitant saisie manuelle des dimensions',
      description: 'Format spécial - Saisie manuelle des dimensions et poids requis'
    }
  ];

  // Fonction pour gérer le changement de format de colis
  const handlePackageFormatChange = (formatId) => {
    setSelectedPackageFormat(formatId);
    
    // Pour le format XL, activer la saisie manuelle
    if (formatId === 'xl') {
      setShowCustomDimensions(true);
      setLength('');
      setWidth('');
      setHeight('');
      setWeight('');
    } else {
      setShowCustomDimensions(false);
      const format = packageFormats.find(f => f.id === formatId);
      if (format) {
        setLength(format.dimensions.length.toString());
        setWidth(format.dimensions.width.toString());
        setHeight(format.dimensions.height.toString());
        setWeight(format.weight.toString());
      }
    }
  };

  // Calculer automatiquement le type de véhicule selon le poids
  const calculateVehicleType = (totalWeight) => {
    if (!totalWeight || isNaN(totalWeight)) return 2; // Moto par défaut
    
    for (const vehicle of VEHICLE_TYPES) {
      const minW = Number(vehicle.min_weight) || 0;
      const maxW = Number(vehicle.max_weight);
      if (typeof maxW === 'number' && isFinite(maxW)) {
        if (totalWeight >= minW && totalWeight < maxW) return vehicle.id;
      } else {
        if (totalWeight >= minW) return vehicle.id;
      }
    }
    return 7; // Camion par défaut
  };

  const getCarrierName = async (carrierId) => {
    try {
      const response = await carrierAPI.getAllCarriers();
    //   console.log('📦 [getCarrierName] → Réponse complète:', response.data);
    //   console.log('📦 [getCarrierName] → ID recherché:', carrierId);
      const carrier = response.data.find(c => c.id === carrierId);
      console.log('📦 [getCarrierName] → Transporteur trouvé:', carrier);
      return carrier ? carrier.nom : `Transporteur ${carrierId}`;
    } catch (error) {
      console.error('Erreur récupération transporteur:', error);
      return `Transporteur ${carrierId}`;
    }
  };

  // Extrait une adresse lisible à partir d'une valeur LocationSearch (objet) ou d'une chaîne
  const getAddressText = (location) => {
    if (!location) return '';
    if (typeof location === 'string') return location;
    if (typeof location === 'object') {
      return location.address || location.full_address || location.name || location.label || location.value || location.city || '';
    }
    return '';
  };

  const getRelayName = async (relayId) => {
    try {
      const response = await relayAPI.getAllRelays();
    //   console.log('🏪 [getRelayName] → Réponse complète:', response.data);
    //   console.log('🏪 [getRelayName] → ID recherché:', relayId);
      const relay = response.data.find(r => r.id === relayId);
    //   console.log('🏪 [getRelayName] → Point relais trouvé:', relay);
      return relay ? relay.nom : `Point relais ${relayId}`;
    } catch (error) {
      console.error('Erreur récupération point relais:', error);
      return `Point relais ${relayId}`;
    }
  };

  const handleTracking = async (e) => {
    e.preventDefault();
    
    if (!trackingNumber.trim()) {
      setTrackingError('Veuillez saisir un numéro de suivi');
      return;
    }

    if (trackingNumber.length < 5) {
      setTrackingError('Le numéro de suivi doit contenir au moins 5 caractères');
      return;
    }

    setIsTrackingLoading(true);
    setTrackingError('');

    try {
      const response = await expeditionAPI.trackExpedition(trackingNumber);
      
      if (response && response.data) {
        setTrackingResult(response.data);
        setShowTrackingModal(true);
      } else {
        setTrackingError('Aucun colis trouvé avec ce numéro d\'expédition');
      }
    } catch (error) {
      console.error('Erreur lors du suivi:', error);
      const msg = error.response?.data?.error;
      if (error.response?.status === 404) {
        setTrackingError(msg || 'Aucun colis trouvé avec ce numéro d\'expédition');
      } else if (error.response?.status === 400) {
        setTrackingError(msg || 'Numéro d\'expédition invalide ou erreur réseau');
        toast.error(msg || 'Numéro d\'expédition invalide');
      } else {
        setTrackingError(msg || 'Erreur de connexion. Veuillez réessayer.');
      }
    } finally {
      setIsTrackingLoading(false);
    }
  };


  const handleEstimation = async (e) => {
    e.preventDefault();
    setIsEstimationLoading(true);
    setEstimationResult(null);
    setShowSimulation(true);

    try {
      // Validation détaillée des champs requis avec messages spécifiques
      if (!serviceType) {
        toast.error('⚠️ Type de service requis');
        setIsEstimationLoading(false);
        setShowSimulation(false);
        return;
      }
      if (!departureCity) {
        toast.error('⚠️ Ville/Commune de départ requise');
        setIsEstimationLoading(false);
        setShowSimulation(false);
        return;
      }
      if (!destinationCity) {
        toast.error('⚠️ Ville/Commune de destination requise');
        setIsEstimationLoading(false);
        setShowSimulation(false);
        return;
      }
      if (!pickupLocation) {
        toast.error('⚠️ Lieu de ramassage requis');
        setIsEstimationLoading(false);
        setShowSimulation(false);
        return;
      }
      if (!deliveryLocation) {
        toast.error('⚠️ Lieu de livraison requis');
        setIsEstimationLoading(false);
        setShowSimulation(false);
        return;
      }
      if (!selectedPackageFormat) {
        toast.error('⚠️ Format de colis requis');
        setIsEstimationLoading(false);
        setShowSimulation(false);
        return;
      }
      if (selectedPackageFormat === 'xl') {
        if (!length || length <= 0) {
          toast.error('⚠️ Longueur requise (doit être supérieure à 0)');
          setIsEstimationLoading(false);
          setShowSimulation(false);
          return;
        }
        if (!width || width <= 0) {
          toast.error('⚠️ Largeur requise (doit être supérieure à 0)');
          setIsEstimationLoading(false);
          setShowSimulation(false);
          return;
        }
        if (!height || height <= 0) {
          toast.error('⚠️ Hauteur requise (doit être supérieure à 0)');
          setIsEstimationLoading(false);
          setShowSimulation(false);
          return;
        }
        if (!weight || weight <= 0) {
          toast.error('⚠️ Poids requis (doit être supérieur à 0)');
          setIsEstimationLoading(false);
          setShowSimulation(false);
          return;
        }
      }

      // Validation du poids total (ne doit pas dépasser 40000kg)
      const totalWeight = parseFloat(weight) || 0;
      if (totalWeight > 40000) {
        toast.error(`⚠️ Limite de poids dépassée !\n\nPoids de l'article : ${totalWeight.toFixed(2)} kg\n\nLa limite maximale est de 40000 kg.`);
        setIsEstimationLoading(false);
        setShowSimulation(false);
        return;
      }

      // Extraire les noms des villes des objets LocationSearch
      const getCityName = (location) => {
        if (!location) return '';
        if (typeof location === 'string') return location;
        if (typeof location === 'object') {
          // Pour LocationSearch, utiliser address qui contient l'adresse complète
          return location.address || location.name || location.label || location.value || location.city || '';
        }
        return '';
      };

      // Calcul distance si coordonnées disponibles dans les objets LocationSearch
      const extractLat = (loc) => (typeof loc === 'object' ? parseFloat(loc?.latitude) : NaN);
      const extractLng = (loc) => (typeof loc === 'object' ? parseFloat(loc?.longitude) : NaN);
      const lat1 = extractLat(pickupLocation);
      const lon1 = extractLng(pickupLocation);
      const lat2 = extractLat(deliveryLocation);
      const lon2 = extractLng(deliveryLocation);
      let distanceKm = 0;
      if ([lat1, lon1, lat2, lon2].every(v => typeof v === 'number' && !isNaN(v))) {
        const R = 6371;
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                  Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                  Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        distanceKm = R * c;
      }

      // Construire un routeText pour aider à la détection ville/commune dans tariffService
      // const originAddress = typeof pickupLocation === 'object' ? (pickupLocation.address || getCityName(pickupLocation)) : getCityName(pickupLocation);
      // const destinationAddress = typeof deliveryLocation === 'object' ? (deliveryLocation.address || getCityName(deliveryLocation)) : getCityName(deliveryLocation);

      // Calculer le type de véhicule automatiquement
      const calculatedVehicleType = calculateVehicleType(totalWeight);
      setSelectedVehicleType(calculatedVehicleType);

      // 🚚 LOGIQUE TRANSPORTEUR : Récupération du transporteur du gérant du point relais si mode relay_point
      let selectedCarrierId = 1; // Transporteur par défaut
      let selectedCarrierCode = 'EVA';
      
      if (deliveryMode === 'relay_point' && selectedRelayPoint) {
        console.log('🏪 Mode point relais détecté - recherche du transporteur du gérant...');
        console.log('   - Point relais sélectionné:', selectedRelayPoint.nom || selectedRelayPoint.name);
        console.log('   - ID du point relais:', selectedRelayPoint.id);
        
        try {
          // Récupérer tous les points relais avec le service API pour vérification
          console.log('🔍 Récupération des détails du point relais via API...');
          const relayResponse = await relayAPI.getAllRelays();
          const allRelays = relayResponse.data;
          
          // Trouver le point relais sélectionné dans la liste complète
          const relayDetails = allRelays.find(relay => relay.id === selectedRelayPoint.id);
          
          if (relayDetails && relayDetails.gerant && relayDetails.gerant.transporteur) {
            selectedCarrierId = relayDetails.gerant.transporteur;
            selectedCarrierCode = 'RELAY';
            console.log('✅ TRANSPORTEUR DU GÉRANT DU POINT DE RELAIS:', selectedCarrierId);
            console.log('   - Gérant:', relayDetails.gerant.first_name, relayDetails.gerant.last_name);
          } else {
            console.warn('⚠️ Point relais sans gérant ou transporteur, utilisation du transporteur par défaut');
          }
        } catch (error) {
          console.error('❌ Erreur lors de la récupération des détails du point relais:', error);
          console.warn('⚠️ Utilisation du transporteur par défaut');
        }
      }

      // Préparer les données pour le calcul de tarif (alignées avec Expedier)
      const shipmentData = {
        departure_city: departureCity || getCityName(pickupLocation),
        destination_city: destinationCity || getCityName(deliveryLocation),
        weight: parseFloat(weight),
        length: parseFloat(length),
        width: parseFloat(width),
        height: parseFloat(height),
        volumeCm3: (parseFloat(length) || 0) * (parseFloat(width) || 0) * (parseFloat(height) || 0),
        carrierId: selectedCarrierId, // ✅ Utilise le transporteur du gérant ou par défaut
        carrierCode: selectedCarrierCode,
        serviceType: serviceType,
        isInsured: (parseFloat(declaredValue) || 0) > 0,
        declaredValue: parseFloat(declaredValue) || 0,
        distance: distanceKm,
        // Coordonnées GPS pour calcul automatique de la distance
        from_latitude: lat1,
        from_longitude: lon1,
        to_latitude: lat2,
        to_longitude: lon2,
        isDepotRelayPoint: false,
        isPickupRelayPoint: deliveryMode === 'relay_point',
        isHolidayWeekend: false,
        vehicleType: VEHICLE_TYPES.find(v => v.id === calculatedVehicleType)?.name.toLowerCase() || 'voiture'
      };

      // console.log('📦 [handleEstimation] → Données d\'expédition:', shipmentData);
      // console.log('📦 [handleEstimation] → pickupLocation type:', typeof pickupLocation, 'value:', pickupLocation);
      // console.log('📦 [handleEstimation] → deliveryLocation type:', typeof deliveryLocation, 'value:', deliveryLocation);
      // console.log('📦 [handleEstimation] → originCity extrait:', shipmentData.originCity);
      // console.log('📦 [handleEstimation] → destinationCity extrait:', shipmentData.destinationCity);
      // console.log('📦 [handleEstimation] → pickupLocation.address:', pickupLocation?.address);
      // console.log('📦 [handleEstimation] → deliveryLocation.address:', deliveryLocation?.address);
      // console.log('📦 [handleEstimation] → declaredValue:', declaredValue);
      // console.log('📦 [handleEstimation] → deliveryMode:', deliveryMode);
      // console.log('📦 [handleEstimation] → serviceType:', serviceType);

      // Calculer le tarif avec le transporteur ID 1
      let result = await calculateTariff(shipmentData);
      
      // Si le transporteur ID 1 n'a pas de tarification, essayer avec ID 4
      if (!result.success) {
        console.log('🔄 [handleEstimation] → Transporteur ID 1 échoué, essai avec ID 4');
        shipmentData.carrierId = 4;
        result = await calculateTariff(shipmentData);
      }

      if (result.success) {
        setEstimationResult({
          success: true,
          price: result.finalPrice,
          currency: result.currency,
          details: {
            weight: shipmentData.weight,
            volume: shipmentData.volumeCm3 / 1000000, // en m³
            basePrice: result.breakdown.basePrice,
            weightPrice: result.breakdown.weightTariff,
            volumePrice: result.breakdown.volumeTariff,
            zonePrice: result.breakdown.zoneTariff,
            cityPrice: result.breakdown.cityTariff,
            factors: result.breakdown.factors,
            tariffGrid: result.details.tariffGrid,
            originCity: result.details.originCity,
            destinationCity: result.details.destinationCity
          }
        });
        setShowEstimationModal(true);
      } else {
        throw new Error('Impossible de calculer le tarif avec les transporteurs disponibles');
      }
    } catch (error) {
      console.error('❌ [handleEstimation] → Erreur estimation:', error);
      
      // Gestion spécifique des erreurs de grille tarifaire
      let errorMessage = error.message;
      if (error.message && (error.message.includes('Aucune grille tarifaire') || error.message.includes('n\'est pas encore configuré'))) {
        // Afficher un modal d'erreur esthétique
        setErrorModalData({
          title: '🚨 Configuration manquante',
          message: error.message,
          type: 'configuration'
        });
        setShowErrorModal(true);
        
        errorMessage = `🚨 Configuration manquante\n\n${error.message}\n\n💡 Choisissez un autre type de service ou contactez l'administrateur.`;
      }
      // Gestion spécifique des erreurs de trajet non disponible
      else if (error.message && (error.message.includes('Service non disponible pour ce trajet') || error.message.includes('Ce trajet n\'existe pas'))) {
        // Afficher un modal d'erreur esthétique
        setErrorModalData({
          title: '🚨 Trajet non disponible',
          message: error.message,
          type: 'route_unavailable'
        });
        setShowErrorModal(true);
        
        errorMessage = `🚨 Trajet non disponible\n\n${error.message}\n\n💡 Choisissez un autre trajet ou type de service.`;
      }
      
      setEstimationResult({
        success: false,
        error: errorMessage
      });
    } finally {
      setIsEstimationLoading(false);
      setShowSimulation(false);
    }
  };

  const handlePickupLocationSelect = (location) => {
    // Stocker l'objet complet pour le calcul
    setPickupLocation(location);
    // Mais afficher seulement l'adresse dans le champ
    console.log('📍 [handlePickupLocationSelect] → Location sélectionnée:', location);
  };

  const handleDeliveryLocationSelect = (location) => {
    // Stocker l'objet complet pour le calcul
    setDeliveryLocation(location);
    // Mais afficher seulement l'adresse dans le champ
    console.log('📍 [handleDeliveryLocationSelect] → Location sélectionnée:', location);
  };

  // getStatusIcon non utilisé → supprimé pour éviter le warning linter

  function normalizeStatusCode(status) {
    if (status === null || status === undefined) return '';
    const statusStr = String(status).trim();
    if (STATUS_LABELS[statusStr]) {
      return statusStr;
    }
    const entry = Object.entries(STATUS_LABELS).find(([, label]) => label.toLowerCase() === statusStr.toLowerCase());
    return entry ? entry[0] : statusStr;
  }

  const getStatusLabel = (status) => {
    const code = normalizeStatusCode(status);
    return STATUS_LABELS[code] || (status ?? '—');
  };

  const getStatusColor = (status) => {
    const code = normalizeStatusCode(status);
    switch (code) {
      case '1':
      case '2':
        return 'bg-green-500';
      case '3':
      case '4':
        return 'bg-green-500';
      case '5':
      case '6':
        return 'bg-green-500';
      case '7':
        return 'bg-purple-500';
      case '8':
        return 'bg-green-500';
      case '9':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const handleCommander = () => {
    // Préparer les données pour la page Expedier
    const expeditionData = {
      pickupLocation: pickupLocation,
      deliveryLocation: deliveryLocation,
      dimensions: {
        length: parseFloat(length),
        width: parseFloat(width),
        height: parseFloat(height)
      },
      weight: parseFloat(weight),
      declaredValue: parseFloat(declaredValue) || 0,
      deliveryMode: deliveryMode,
      serviceType: serviceType,
      estimatedPrice: estimationResult?.price || 0,
      estimationDetails: estimationResult?.details || {}
    };

    // Stocker les données dans localStorage pour les récupérer dans Expedier
    localStorage.setItem('ksl_expedition_data', JSON.stringify(expeditionData));
    
    // Naviguer vers la page Expedier
    navigate('/expedier');
  };

  // Forcer automatiquement le mode "relais" quand "interurbaine" est sélectionné
  useEffect(() => {
    if (serviceType === 'interurbaine') {
      setDeliveryMode('relais');
    }
  }, [serviceType]);

  return (
    <>
            {/* Hero Section */}
      <section className="py-7 md:py-15 bg-cover bg-center bg-no-repeat relative" style={{ backgroundImage: 'url(/hero.jpg)' }}>
        <div className="absolute inset-0 bg-black bg-opacity-40 backdrop-blur-sm"></div>
        <div className="relative z-10">
          <div className="container-ksl">
            <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white dark:text-white mb-4 sm:mb-6 md:mb-8">
                Simplifiez Votre Livraison
              </h1>

            {/* Section de suivi de colis */}
            <div className="bg-white dark:bg-dark-bg-secondary rounded-xl shadow-xl p-6 mb-8 border border-gray-200 dark:border-gray-700">
              {/* Bouton Expédition en mode responsive - visible seulement sur mobile */}
              <div className="block sm:hidden mb-4">
                <button
                  onClick={handleCommander}
                  className="w-full px-4 py-3 bg-ksl-red text-white font-medium rounded-lg hover:bg-ksl-red-dark transition-colors duration-200 border border-ksl-red flex items-center justify-center gap-2"
                >
                  <Package className="w-4 h-4" />
                  Expédition
                </button>
              </div>
              
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Suivez votre colis
              </h2>
                <form onSubmit={handleTracking} className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <input
                  type="text"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  placeholder="Numéro d'expédition (ex: KSL-12345)"
                    className="flex-1 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-ksl-red"
                />
                <button
                  type="submit"
                  disabled={isTrackingLoading}
                    className="px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors duration-200 border border-green-600 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isTrackingLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                  {isTrackingLoading ? 'Recherche...' : 'Suivre'}
                </button>
              </form>
              {trackingError && (
                <div className="mt-3 text-sm text-red-600 dark:text-red-400">
                  {trackingError}
                </div>
              )}
              <div className="mt-4 text-sm text-gray-600 dark:text-gray-300">
                Vous pouvez aussi consulter la page dédiée au suivi :
                <button
                  type="button"
                  onClick={() => navigate('/tracking')}
                  className="ml-2 text-ksl-red hover:text-ksl-red-dark underline"
                >
                  Ouvrir la page suivi
                </button>
              </div>
            </div>

              <p className="text-sm sm:text-base md:text-lg text-white/90 dark:text-gray-300 mb-4 sm:mb-6 md:mb-8 max-w-3xl mx-auto px-2 sm:px-4">
                Une solution complète pour gérer vos expéditions, points de relais, et paiements à la livraison en toute simplicité.
              </p>

              {/* Bouton Expédier maintenant */}
              <div className="hidden md:flex justify-center">
                <Link
                  to="/expedier"
                  className="inline-flex items-center gap-3 px-6 sm:px-8 py-3 sm:py-4 bg-ksl-red text-white rounded-xl hover:bg-ksl-red-dark transition-all duration-300 transform hover:scale-105 active:scale-95 text-base sm:text-lg font-semibold shadow-xl hover:shadow-2xl"
                >
                  <Package className="w-5 h-5 sm:w-6 sm:h-6" />
                  <span>Expédier maintenant</span>
                  <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6" />
                </Link>
              </div>

            {/* Section d'estimation rapide - MASQUÉE */}
              <div className="hidden bg-white dark:bg-dark-bg-secondary rounded-xl shadow-xl p-3 sm:p-4 md:p-6 border border-gray-200 dark:border-gray-700">
                
                {/* Simulation animée */}
                {showSimulation && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fade-in">
                    <div className="bg-white dark:bg-dark-bg-secondary rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl animate-slide-up">
                      <div className="text-center space-y-6">
                        {/* Icône de calcul animée */}
                        <div className="relative">
                          <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto animate-pulse">
                            <Calculator className="w-10 h-10 text-white" />
                          </div>
                          <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full animate-ping opacity-20"></div>
                        </div>
                        
                        {/* Titre */}
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                            Calcul en cours...
                          </h3>
                          <p className="text-gray-600 dark:text-gray-300">
                            Simulation de votre tarif de livraison
                          </p>
                        </div>
                        
                        {/* Barre de progression */}
                        <div className="space-y-3">
                          <div className="flex justify-between text-sm text-gray-600 dark:text-gray-300">
                            <span>Analyse des données</span>
                            <span className="animate-pulse">✓</span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full animate-pulse" style={{width: '60%'}}></div>
                          </div>
                          
                          <div className="flex justify-between text-sm text-gray-600 dark:text-gray-300">
                            <span>Calcul du tarif</span>
                            <span className="animate-pulse">⏳</span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div className="bg-gradient-to-r from-purple-500 to-pink-600 h-2 rounded-full animate-pulse" style={{width: '80%'}}></div>
                          </div>
                          
                          <div className="flex justify-between text-sm text-gray-600 dark:text-gray-300">
                            <span>Finalisation</span>
                            <span className="animate-pulse">⏳</span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div className="bg-gradient-to-r from-pink-500 to-red-600 h-2 rounded-full animate-pulse" style={{width: '40%'}}></div>
                          </div>
                        </div>
                        
                        {/* Messages de statut */}
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                            <span>Vérification des dimensions...</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                            <span>Calcul du volume et poids...</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                            <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                            <span>Application des tarifs...</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Header avec bouton toggle */}
                <div className="flex items-center justify-between mb-3 sm:mb-4 md:mb-6">
                  <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 dark:text-white animate-fade-in">
                Estimation Rapide
              </h2>
                  <button
                    onClick={() => setShowEstimationForm(!showEstimationForm)}
                    className="flex items-center gap-2 px-4 py-2 bg-ksl-red text-white rounded-lg hover:bg-ksl-red-dark transition-all duration-300 transform hover:scale-105 active:scale-95 text-sm font-medium shadow-md hover:shadow-lg"
                  >
                    {showEstimationForm ? (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                        Masquer
                      </>
                    ) : (
                      <>
                        <Calculator className="w-4 h-4" />
                        Afficher l'estimation
                      </>
                    )}
                  </button>
                </div>
                                {showEstimationForm && (
                  <div className="animate-slide-down">
                    <form onSubmit={handleEstimation} className="space-y-3 sm:space-y-4">
                
                {/* Sélection du type de service - AU DÉBUT */}
                <div className="mb-4">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                    Type de service *
                  </label>
                  <select
                    value={serviceType}
                    onChange={(e) => setServiceType(e.target.value)}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-ksl-red"
                  >
                    <option value="">Sélectionnez un type de service</option>
                    <option value="flash">⚡ Chrono - 2h à 4h</option>
                    <option value="express">🎯 Express - 24H - 0-1 JOUR</option>
                    <option value="standard">📦 Standard - 24h à 48h</option>
                    <option value="economique">💰 Économique - 48h à 78h</option>
                    <option value="interurbaine">🌍 Interurbaine - 86H - 2-4 JOURS</option>
                  </select>
                </div>

                {/* Sélection des villes/communes selon le type de service */}
                {serviceType && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                        {serviceType === 'interurbaine' ? 'Ville de départ *' : 'Commune de départ *'}
                      </label>
                      <select
                        value={departureCity}
                        onChange={(e) => {
                          setDepartureCity(e.target.value);
                          // Pré-remplir l'adresse de ramassage
                          if (e.target.value) {
                            const address = serviceType === 'interurbaine' 
                              ? `${e.target.value}, Côte d'Ivoire`
                              : `${e.target.value}, Abidjan, Côte d'Ivoire`;
                            setPickupLocation(address);
                            console.log('📍 Adresse de ramassage pré-remplie:', address);
                          }
                        }}
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-ksl-red"
                      >
                        <option value="">
                          {serviceType === 'interurbaine' 
                            ? 'Sélectionnez une ville de départ' 
                            : 'Sélectionnez une commune de départ'
                          }
                        </option>
                        {(serviceType === 'interurbaine' ? ALL_CITIES : ABIDJAN_COMMUNES).map((location) => (
                          <option key={location} value={location}>{location}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                        {serviceType === 'interurbaine' ? 'Ville de destination *' : 'Commune de destination *'}
                      </label>
                      <select
                        value={destinationCity}
                        onChange={(e) => {
                          setDestinationCity(e.target.value);
                          // Pré-remplir l'adresse de livraison
                          if (e.target.value) {
                            const address = serviceType === 'interurbaine' 
                              ? `${e.target.value}, Côte d'Ivoire`
                              : `${e.target.value}, Abidjan, Côte d'Ivoire`;
                            setDeliveryLocation(address);
                            console.log('📍 Adresse de livraison pré-remplie:', address);
                          }
                        }}
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-ksl-red"
                      >
                        <option value="">
                          {serviceType === 'interurbaine' 
                            ? 'Sélectionnez une ville de destination' 
                            : 'Sélectionnez une commune de destination'
                          }
                        </option>
                        {(serviceType === 'interurbaine' ? ALL_CITIES : ABIDJAN_COMMUNES).map((location) => (
                          <option key={location} value={location}>{location}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                      Lieu de ramassage
                    </label>
                    <LocationSearch
                          value={typeof pickupLocation === 'object' ? pickupLocation?.address || '' : pickupLocation}
                          onChange={(value) => {
                            // Si c'est une chaîne (saisie manuelle), on la stocke directement
                            if (typeof value === 'string') {
                              setPickupLocation(value);
                            }
                            // Si c'est un objet (sélection depuis la liste), on le stocke
                            else if (typeof value === 'object') {
                              setPickupLocation(value);
                            }
                          }}
                      onLocationSelect={handlePickupLocationSelect}
                      placeholder="Adresse de ramassage"
                    />
                  </div>
                  <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                      Lieu de Livraison
                    </label>
                    <LocationSearch
                          value={typeof deliveryLocation === 'object' ? deliveryLocation?.address || '' : deliveryLocation}
                          onChange={(value) => {
                            // Si c'est une chaîne (saisie manuelle), on la stocke directement
                            if (typeof value === 'string') {
                              setDeliveryLocation(value);
                            }
                            // Si c'est un objet (sélection depuis la liste), on le stocke
                            else if (typeof value === 'object') {
                              setDeliveryLocation(value);
                            }
                          }}
                      onLocationSelect={handleDeliveryLocationSelect}
                      placeholder="Adresse de livraison"
                    />
              </div>
            </div>


                {/* Sélecteur de format de colis */}
                <div className="mb-4">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                    Format de colis *
                  </label>
                  <select
                    value={selectedPackageFormat}
                    onChange={(e) => handlePackageFormatChange(e.target.value)}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-ksl-red"
                  >
                    <option value="">Sélectionnez un format</option>
                    {packageFormats.map((format) => (
                      <option key={format.id} value={format.id}>
                        {format.name} - {format.description}
                      </option>
                    ))}
                  </select>
                  {selectedPackageFormat && selectedPackageFormat !== 'xl' && (
                    <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <p className="text-xs text-green-700 dark:text-blue-300">
                        <strong>Format sélectionné :</strong> {packageFormats.find(f => f.id === selectedPackageFormat)?.name}
                      </p>
                      <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                        Dimensions : {packageFormats.find(f => f.id === selectedPackageFormat)?.dimensions.length} × {packageFormats.find(f => f.id === selectedPackageFormat)?.dimensions.width} × {packageFormats.find(f => f.id === selectedPackageFormat)?.dimensions.height} cm
                      </p>
                      <p className="text-xs text-blue-600 dark:text-blue-400">
                        Poids : {packageFormats.find(f => f.id === selectedPackageFormat)?.weight} kg
                      </p>
                    </div>
                  )}
                </div>

                {/* Champs de dimensions - affichés seulement pour XL ou si showCustomDimensions est true */}
                {(showCustomDimensions || selectedPackageFormat === 'xl') && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                        Longueur (L) en cm
                      </label>
                  <div className="relative">
                            <Ruler className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          value={length}
                          onChange={(e) => setLength(e.target.value)}
                            className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-ksl-red"
                            placeholder="0.0"
                    />
                  </div>
                      </div>
                      <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                        Largeur (l) en cm
                      </label>
                      <div className="relative">
                          <Ruler className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          value={width}
                          onChange={(e) => setWidth(e.target.value)}
                            className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-ksl-red"
                            placeholder="0.0"
                        />
                      </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                        Hauteur (h) en cm
                      </label>
                      <div className="relative">
                          <Ruler className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          value={height}
                          onChange={(e) => setHeight(e.target.value)}
                            className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-ksl-red"
                            placeholder="0.0"
                        />
                      </div>
                      </div>
                      <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                        Poids en kg
                      </label>
                      <div className="relative">
                          <Package className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          value={weight}
                          onChange={(e) => setWeight(e.target.value)}
                            className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-ksl-red"
                            placeholder="0.0"
                        />
                      </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Sélection du type de véhicule */}
                <div className="mb-4">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                    Type de véhicule (Équipement) *
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
                    {VEHICLE_TYPES.map((vehicle) => (
                      <button
                        key={vehicle.id}
                        type="button"
                        onClick={() => setSelectedVehicleType(vehicle.id)}
                        className={`p-3 rounded-lg border-2 transition-all duration-300 text-left ${
                          selectedVehicleType === vehicle.id
                            ? 'border-ksl-red bg-ksl-red/10 text-ksl-red'
                            : 'border-gray-200 hover:border-gray-300 dark:border-gray-600 dark:hover:border-gray-500'
                        }`}
                      >
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-lg">{vehicle.icon}</span>
                          <span className="text-xs sm:text-sm font-medium">{vehicle.name}</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {vehicle.min_weight}-{vehicle.max_weight}kg
                        </div>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Le type de véhicule est calculé automatiquement selon le poids, mais vous pouvez le modifier manuellement.
                  </p>
                </div>
                
                  {/* Nouveaux champs */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                        Valeur déclarée (FCFA)
                      </label>
                      <div className="relative">
                        <Calculator className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                        <input
                          type="number"
                          step="1000"
                          min="0"
                          value={declaredValue}
                          onChange={(e) => setDeclaredValue(e.target.value)}
                          className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-ksl-red"
                          placeholder="0"
                        />
                </div>
            </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Mode d'expédition *
                      </label>
                      
                      <div className={cn(
                        "gap-2",
                        serviceType === 'interurbaine' 
                          ? "grid grid-cols-1" 
                          : "grid grid-cols-2"
                      )}>
                        {/* Carte Livraison à domicile - masquée pour interurbaine */}
                        {serviceType !== 'interurbaine' && (
                          <div 
                            className={`relative cursor-pointer rounded-lg border-2 p-2 transition-all duration-200 ${
                              deliveryMode === 'domicile' 
                                ? 'border-ksl-red bg-ksl-red/5 dark:bg-ksl-red/10' 
                                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-bg-secondary hover:border-ksl-red/50'
                            }`}
                            onClick={() => setDeliveryMode('domicile')}
                          >
                          <div className="flex flex-col items-center text-center space-y-1">
                            {/* Icône maison */}
                            <div className="w-8 h-8 flex items-center justify-center">
                              <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                              </svg>
          </div>
                            <span className="text-xs font-medium text-gray-900 dark:text-white">
                              Livraison à domicile
                            </span>
        </div>
                          
                            {/* Indicateur de sélection */}
                            {deliveryMode === 'domicile' && (
                              <div className="absolute top-1 right-1 w-3 h-3 bg-ksl-red rounded-full flex items-center justify-center">
                                <svg className="w-1.5 h-1.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Carte Dépôt en point relais */}
                        <div 
                          className={`relative cursor-pointer rounded-lg border-2 p-2 transition-all duration-200 ${
                            deliveryMode === 'relais' 
                              ? 'border-ksl-red bg-ksl-red/5 dark:bg-ksl-red/10' 
                              : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-bg-secondary hover:border-ksl-red/50'
                          }`}
                          onClick={() => setDeliveryMode('relais')}
                        >
                          <div className="flex flex-col items-center text-center space-y-1">
                            {/* Icône magasin avec badge */}
                            <div className="relative w-8 h-8 flex items-center justify-center">
                              <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                              </svg>
                              {/* Badge 24h */}
                              <div className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full flex items-center justify-center">
                                <span className="text-xs font-bold text-white">24</span>
                              </div>
                            </div>
                            <span className="text-xs font-medium text-gray-900 dark:text-white">
                              Dépôt en point relais
                            </span>
            </div>

                          {/* Indicateur de sélection */}
                          {deliveryMode === 'relais' && (
                            <div className="absolute top-1 right-1 w-3 h-3 bg-ksl-red rounded-full flex items-center justify-center">
                              <svg className="w-1.5 h-1.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Message conditionnel */}
                      {serviceType === 'interurbaine' ? (
                        <p className="text-xs text-orange-600 dark:text-orange-400 mt-3 text-center">
                          🌍 Service interurbaine : Seul le dépôt en point relais est disponible
                        </p>
                      ) : (
                        <p className="text-xs text-gray-500 mt-3 text-center">
                          Choisissez entre livraison directe ou dépôt en point relais
                        </p>
                      )}
                  </div>
                  </div>

                
                <div className="text-center">
                  <button
                    type="submit"
                      disabled={isEstimationLoading}
                      className="px-6 sm:px-8 py-2 sm:py-3 text-sm sm:text-base bg-ksl-red text-white font-medium rounded-lg hover:bg-ksl-red-dark transition-colors duration-200 border border-ksl-red flex items-center justify-center gap-2 mx-auto disabled:opacity-50"
                  >
                      {isEstimationLoading ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                    <Calculator className="w-4 h-4" />
                      )}
                      {isEstimationLoading ? 'Calcul...' : 'Estimer le coût'}
                  </button>
                </div>
              </form>

                                
              </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modal d'estimation */}
      {showEstimationModal && estimationResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 animate-fade-in">
          <div className="bg-white dark:bg-dark-bg-secondary rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto animate-slide-up">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center animate-pulse">
                  <Calculator className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Estimation de coût
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Calcul terminé avec succès
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowEstimationModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-2"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {estimationResult.success ? (
                <div className="flex flex-col gap-8 md:flex-row md:items-stretch md:gap-6">
                  {/* Prix principal */}
                  <div className="text-center bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-8 border-2 border-green-200 dark:border-green-700">
                    <div className="text-4xl font-bold text-green-900 dark:text-green-100 animate-bounce mb-2">
                      {estimationResult.price.toLocaleString()} {estimationResult.currency}
                    </div>
                    <div className="text-lg text-green-700 dark:text-green-300">
                      Prix total estimé
                    </div>
                  </div>

                  {/* Détails en grille */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Informations de base */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                        📦 Détails du colis
                      </h3>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                          <span className="text-gray-600 dark:text-gray-300">Poids</span>
                          <span className="font-semibold text-gray-900 dark:text-white">{estimationResult.details.weight} kg</span>
                        </div>
                        <div className="flex justify-between items-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                          <span className="text-gray-600 dark:text-gray-300">Volume</span>
                          <span className="font-semibold text-gray-900 dark:text-white">{estimationResult.details.volume.toFixed(3)} m³</span>
                        </div>
                        <div className="flex justify-between items-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                          <span className="text-gray-600 dark:text-gray-300">Service</span>
                          <span className="font-semibold text-gray-900 dark:text-white capitalize">{serviceType}</span>
                        </div>
                        <div className="flex justify-between items-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                          <span className="text-gray-600 dark:text-gray-300">Mode de livraison</span>
                          <span className="font-semibold text-gray-900 dark:text-white">{deliveryMode === 'domicile' ? 'À domicile' : 'Point relais'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Informations tarifaires */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                        💰 Détails tarifaires
                      </h3>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                          <span className="text-gray-600 dark:text-gray-300">Trajet</span>
                          <span className="font-semibold text-gray-900 dark:text-white text-sm">
                            {getAddressText(pickupLocation)} → {getAddressText(deliveryLocation)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                          <span className="text-gray-600 dark:text-gray-300">Grille tarifaire</span>
                          <span className="font-semibold text-gray-900 dark:text-white">{estimationResult.details.tariffGrid}</span>
                        </div>
                        <div className="flex justify-between items-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                          <span className="text-gray-600 dark:text-gray-300">Prix de base</span>
                          <span className="font-semibold text-gray-900 dark:text-white">{estimationResult.details.basePrice} FCFA</span>
                        </div>
                        <div className="flex justify-between items-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                          <span className="text-gray-600 dark:text-gray-300">Tarif poids</span>
                          <span className="font-semibold text-gray-900 dark:text-white">{estimationResult.details.weightPrice} FCFA</span>
                        </div>
                        {declaredValue > 0 && (
                          <div className="flex justify-between items-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700">
                            <span className="text-green-700 dark:text-green-300">Valeur déclarée</span>
                            <span className="font-semibold text-green-900 dark:text-green-100">{parseFloat(declaredValue).toLocaleString()} FCFA</span>
                </div>
              )}
                        {declaredValue > 0 && (
                          <div className="flex justify-between items-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700">
                            <span className="text-green-700 dark:text-green-300">Assurance</span>
                            <span className="font-semibold text-green-900 dark:text-green-100 flex items-center gap-1">
                              ✓ Incluse
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
            </div>

                  {/* Boutons d'action */}
                  <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                      onClick={handleCommander}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Commander maintenant
                    </button>
                                        <button 
                      onClick={() => window.location.reload()}
                      className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Actualiser
              </button>
                  </div>
                </div>
              ) : (
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center mx-auto">
                    <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-red-900 dark:text-red-100">
                    Erreur d'estimation
                  </h3>
                  <p className="text-red-600 dark:text-red-400">
                    {estimationResult.error}
                  </p>
                  <button 
                    onClick={() => window.location.reload()}
                    className="bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200"
                  >
                    Actualiser
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de suivi - même timeline que RelayPackages */}
      {showTrackingModal && trackingResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 animate-fade-in">
          <div className="bg-white dark:bg-dark-bg-secondary rounded-xl shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-y-auto animate-slide-up">
            <TrackingTimelineBlock
              data={trackingResult}
              isLoading={false}
              recipientDisplay={
                trackingResult?.recipient?.recipient_name ||
                [trackingResult?.recipient?.recipient_first_name, trackingResult?.recipient?.recipient_last_name].filter(Boolean).join(' ').trim() ||
                (trackingResult?.adresse_destinataire ? extractAddressInfo(trackingResult.adresse_destinataire).name : null) ||
                '—'
              }
              onClose={() => setShowTrackingModal(false)}
              onCopyTracking={async (num) => {
                try {
                  await navigator.clipboard.writeText(num || '');
                  toast.success('Numéro copié !');
                } catch {
                  toast.error('Erreur lors de la copie');
                }
              }}
            />
          </div>
        </div>
      )}

      {/* Chiffres clés */}
      <section className="hidden py-16 bg-gray-50 dark:bg-dark-bg-secondary">
        <div className="container-ksl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white dark:bg-dark-bg rounded-xl p-8 text-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 dark:border-gray-700">
              <div className="w-16 h-16 bg-ksl-red/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="w-8 h-8 text-ksl-red" />
              </div>
              <div className="text-4xl font-bold text-ksl-red mb-2">1000+</div>
              <div className="text-gray-600 dark:text-gray-300 font-medium">Colis livrés chaque mois</div>
            </div>
            <div className="bg-white dark:bg-dark-bg rounded-xl p-8 text-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 dark:border-gray-700">
              <div className="w-16 h-16 bg-ksl-red/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Truck className="w-8 h-8 text-ksl-red" />
              </div>
              <div className="text-4xl font-bold text-ksl-red mb-2">50+</div>
              <div className="text-gray-600 dark:text-gray-300 font-medium">Points de relais connectés</div>
            </div>
            <div className="bg-white dark:bg-dark-bg rounded-xl p-8 text-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 dark:border-gray-700">
              <div className="w-16 h-16 bg-ksl-red/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-ksl-red" />
              </div>
              <div className="text-4xl font-bold text-ksl-red mb-2">95%</div>
              <div className="text-gray-600 dark:text-gray-300 font-medium">Clients satisfaits</div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Nos Solutions */}
      <section className="hidden py-16 md:py-20 bg-white dark:bg-dark-bg">
        <div className="container-ksl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Nos Solutions
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Des solutions adaptées à vos besoins spécifiques en matière de logistique
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Expédition Facile */}
            <div className="bg-white dark:bg-dark-bg-secondary rounded-lg p-6 border border-gray-200 dark:border-gray-700 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 relative overflow-hidden">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-ksl-red"></div>
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/20 rounded-full flex items-center justify-center mb-4">
                  <Box className="w-8 h-8 text-amber-600 dark:text-amber-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  Expédition Facile
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
                  Créez des étiquettes d'expédition et choisissez le meilleur transporteur en quelques clics.
                </p>
                <Link to="/solutions/expedition" className="text-ksl-red font-medium hover:text-ksl-red-dark transition-colors">
                  En savoir plus →
                </Link>
              </div>
            </div>

            {/* Points de Relais Connectés */}
            <div className="bg-white dark:bg-dark-bg-secondary rounded-lg p-6 border border-gray-200 dark:border-gray-700 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 relative overflow-hidden">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-ksl-red"></div>
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mb-4">
                  <Building className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  Points de Relais Connectés
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
                  Dépôt, retrait et gestion de stock en temps réel dans notre réseau de points relais.
                </p>
                <Link to="/solutions/relais" className="text-ksl-red font-medium hover:text-ksl-red-dark transition-colors">
                  En savoir plus →
                </Link>
              </div>
            </div>

            {/* Cash on Delivery */}
            <div className="bg-white dark:bg-dark-bg-secondary rounded-lg p-6 border border-gray-200 dark:border-gray-700 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 relative overflow-hidden">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-ksl-red"></div>
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900/20 rounded-full flex items-center justify-center mb-4">
                  <DollarSign className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  Cash on Delivery
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
                  Solution de paiement sécurisée à la livraison pour plus de confiance.
                </p>
                <Link to="/solutions/paiement" className="text-ksl-red font-medium hover:text-ksl-red-dark transition-colors">
                  En savoir plus →
                </Link>
              </div>
            </div>

            {/* Gestion des Retours */}
            <div className="bg-white dark:bg-dark-bg-secondary rounded-lg p-6 border border-gray-200 dark:border-gray-700 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 relative overflow-hidden">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-ksl-red"></div>
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-cyan-100 dark:bg-cyan-900/20 rounded-full flex items-center justify-center mb-4">
                  <RotateCcw className="w-8 h-8 text-cyan-600 dark:text-cyan-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  Gestion des Retours
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
                  Simplifiez la gestion des retours pour vos clients et votre entreprise.
                </p>
                <Link to="/solutions/retours" className="text-ksl-red font-medium hover:text-ksl-red-dark transition-colors">
                  En savoir plus →
                </Link>
              </div>
            </div>

            {/* Suivi et Traçabilité */}
            <div className="bg-white dark:bg-dark-bg-secondary rounded-lg p-6 border border-gray-200 dark:border-gray-700 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 relative overflow-hidden">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-ksl-red"></div>
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mb-4">
                  <Search className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  Suivi et Traçabilité
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
                  Suivez vos colis en temps réel et informez vos clients à chaque étape.
                </p>
                <Link to="/solutions/suivi" className="text-ksl-red font-medium hover:text-ksl-red-dark transition-colors">
                  En savoir plus →
                </Link>
              </div>
            </div>

            {/* Marketplace Logistique */}
            <div className="bg-white dark:bg-dark-bg-secondary rounded-lg p-6 border border-gray-200 dark:border-gray-700 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 relative overflow-hidden">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-ksl-red"></div>
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-ksl-red to-ksl-red-dark rounded-full flex items-center justify-center mb-4">
                  <Rocket className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  Marketplace Logistique
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
                  Bientôt disponible : Une place de marché pour tous vos besoins logistiques.
                </p>
                <span className="text-ksl-red font-medium">
                  Bientôt disponible →
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services de livraison */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-white dark:from-dark-bg-secondary dark:to-dark-bg">
        <div className="container-ksl">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
              NOS SERVICES DE LIVRAISONS
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto px-2 sm:px-0">
              Trouvez la formule qui correspond à vos besoins professionnels ou personnels.
            </p>
          </div>

          <div className="space-y-8">
            {/* Première ligne - 2 cartes centrées */}
            <div className="flex justify-center">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 max-w-4xl">
                <div className="bg-white dark:bg-dark-bg rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-red-500">
                  <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 dark:bg-purple-900/20 rounded-lg mb-3 sm:mb-4">
                    <Bike className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3">
                    Livraison Chrono
                  </h3>
                  <div className="space-y-2">
                    <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
                      Idéale pour les livraisons très urgentes
                    </p>
                    <div className="flex justify-between text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                      <span>Zone: Locale</span>
                      <span>Délai: 0 à 2h</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-dark-bg rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-red-500">
                  <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 dark:bg-purple-900/20 rounded-lg mb-3 sm:mb-4">
                    <Bike className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3">
                    Livraison Express
                  </h3>
                  <div className="space-y-2">
                    <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
                      Idéale pour les livraisons urgentes
                    </p>
                    <div className="flex justify-between text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                      <span>Zone: Locale</span>
                      <span>Délai: en 24h</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Deuxième ligne - 4 cartes */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              <div className="bg-white dark:bg-dark-bg rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-center w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-lg mb-4">
                  <Bike className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  Livraison Standard
                </h3>
                <div className="space-y-2">
                  <p className="text-gray-600 dark:text-gray-300">
                    Un bon compromis entre rapidité et coût
                  </p>
                  <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                    <span>Zone: Locale</span>
                    <span>Délai: 1 à 2 jours ouvrés</span>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-dark-bg rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-center w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-lg mb-4">
                  <Bike className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  Livraison Éco
                </h3>
                <div className="space-y-2">
                  <p className="text-gray-600 dark:text-gray-300">
                    Pour vos envois non urgents, à petit prix
                  </p>
                  <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                    <span>Zone: Locale</span>
                    <span>Délai: 2 à 3 jours ouvrés</span>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-dark-bg rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-center w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-lg mb-4">
                  <Bike className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  Livraison Interurbaine
                </h3>
                <div className="space-y-2">
                  <p className="text-gray-600 dark:text-gray-300">
                    Pour les envois entre villes ou à l'intérieur du pays
                  </p>
                  <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                    <span>Zone: Interurbaine</span>
                    <span>Délai: 2 à 7 jours</span>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-dark-bg rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-center w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg mb-4">
                  <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  Livraison Sous-régionale
                </h3>
                <div className="space-y-2">
                  <p className="text-gray-600 dark:text-gray-300">
                    Pour vos envois vers les pays de la CEDEAO
                  </p>
                  <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                    <span>Zone: Internationale</span>
                    <span>Délai: 5 à 14 jours</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Pour qui? */}
      <section className="py-16 md:py-20 bg-white dark:bg-dark-bg">
        <div className="container-ksl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Pour qui?
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Des solutions adaptées à différents profils d'utilisateurs et secteurs d'activité
            </p>
          </div>

          <div className="space-y-8">
            {/* Première ligne - 2 cartes */}
            <div className="flex justify-center">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
                <div className="bg-white dark:bg-dark-bg rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-red-500">
                  <div className="flex items-center justify-center w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg mb-4">
                    <User className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                    Particuliers
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    Envoyez et recevez des colis facilement, sans tracas administratifs.
                  </p>
                </div>

                <div className="bg-white dark:bg-dark-bg rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-red-500">
                  <div className="flex items-center justify-center w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg mb-4">
                    <Building className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                    Entreprises
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    Optimisez votre chaîne logistique et réduisez vos coûts d'expédition.
                  </p>
                </div>
              </div>
            </div>

            {/* Deuxième ligne - 3 cartes */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white dark:bg-dark-bg rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-red-500">
                <div className="flex items-center justify-center w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg mb-4">
                  <ShoppingCart className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  E-commerçants
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Améliorez votre service client avec des livraisons rapides et fiables.
                </p>
              </div>

              <div className="bg-white dark:bg-dark-bg rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-red-500">
                <div className="flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-lg mb-4">
                  <MapPin className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  Points de relais
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Générez des revenus supplémentaires en devenant un point de collecte et livraison.
                </p>
              </div>

              <div className="bg-white dark:bg-dark-bg rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-red-500">
                <div className="flex items-center justify-center w-12 h-12 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg mb-4">
                  <Truck className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  Transporteurs
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Augmentez votre volume d'affaires en rejoignant notre réseau de transport.
                </p>
              </div>
            </div>
          </div>
          
          {/* Bouton d'action */}
          <div className="text-center mt-12">
            <Link 
              to="/pour-qui" 
              className="inline-flex items-center justify-center px-8 py-3 bg-ksl-red text-white font-medium rounded-lg hover:bg-ksl-red-dark transition-all duration-200 group shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              Découvrir pour qui
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Section Fonctionnalités */}
      <section className="hidden py-16 md:py-20 bg-gray-50 dark:bg-dark-bg-secondary">
        <div className="container-ksl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Fonctionnalités
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Des outils puissants pour optimiser et simplifier votre gestion logistique
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {/* Impression d'étiquettes d'expédition */}
            <div className="bg-white dark:bg-dark-bg rounded-lg p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-pink-100 dark:bg-pink-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Printer className="w-8 h-8 text-pink-600 dark:text-pink-400" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                Impression d'étiquettes d'expédition
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                Générez et imprimez facilement vos étiquettes d'expédition personnalisées.
              </p>
            </div>

            {/* Intégration API complète */}
            <div className="bg-white dark:bg-dark-bg rounded-lg p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <ArrowLeftRight className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                Intégration API complète
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                Connectez votre système e-commerce avec notre API pour automatiser vos processus.
              </p>
            </div>

            {/* Tableaux de bord personnalisés */}
            <div className="bg-white dark:bg-dark-bg rounded-lg p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-pink-100 dark:bg-pink-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-8 h-8 text-pink-600 dark:text-pink-400" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                Tableaux de bord personnalisés
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                Suivez toutes vos activités logistiques grâce à nos tableaux de bord client et relais.
              </p>
            </div>

            {/* Module de paiement intégré */}
            <div className="bg-white dark:bg-dark-bg rounded-lg p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                Module de paiement intégré
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                Solution de paiement sécurisée pour vos transactions cash on delivery.
              </p>
            </div>

            {/* Statistiques en temps réel */}
            <div className="bg-white dark:bg-dark-bg rounded-lg p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-pink-100 dark:bg-pink-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-pink-600 dark:text-pink-400" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                Statistiques en temps réel
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                Analysez votre performance logistique avec des rapports détaillés.
              </p>
            </div>

            {/* Application Mobile */}
            <div className="bg-white dark:bg-dark-bg rounded-lg p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Smartphone className="w-8 h-8 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                Application Mobile
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                Prochainement : Gérez vos expéditions depuis votre smartphone.
              </p>
            </div>
          </div>

          {/* Bouton d'action */}
          <div className="text-center">
            <Link 
              to="/fonctionnalites" 
              className="inline-flex items-center justify-center px-8 py-3 bg-ksl-red text-white font-medium rounded-lg hover:bg-ksl-red-dark transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              Découvrir toutes nos fonctionnalités
            </Link>
          </div>
        </div>
      </section>

   

      {/* Bannière CTA */}
      <section className="py-16 md:py-20 bg-ksl-red">
        <div className="container-ksl">
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Prêt à simplifier votre logistique ?
            </h2>
            <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
              Rejoignez LMS Katian et transformez votre manière de gérer les expéditions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact" 
                className="inline-flex items-center justify-center px-8 py-3 bg-white text-ksl-red font-medium rounded-lg hover:bg-gray-100 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                Contactez-nous
              </Link>
              <Link 
                to="/tarifs" 
                className="inline-flex items-center justify-center px-8 py-3 bg-ksl-red text-white font-medium rounded-lg border-2 border-white hover:bg-white hover:text-ksl-red transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                Voir les tarifs
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Section Nos Partenaires */}
      <section className="hidden py-16 md:py-20 bg-gray-50 dark:bg-dark-bg-secondary">
        <div className="container-ksl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Nos Partenaires
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Nous collaborons avec les meilleurs transporteurs et constructeurs pour vous offrir un service de qualité
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {/* Transport Express */}
            <div className="bg-white dark:bg-dark-bg rounded-lg p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Truck className="w-8 h-8 text-orange-600 dark:text-orange-400" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                Transport Express
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Transporteur
              </p>
            </div>

            {/* Fast Delivery */}
            <div className="bg-white dark:bg-dark-bg rounded-lg p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bike className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                Fast Delivery
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Transporteur
              </p>
            </div>

            {/* EcoMobility */}
            <div className="bg-white dark:bg-dark-bg rounded-lg p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bike className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                EcoMobility
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Constructeur
              </p>
            </div>

            {/* Green Logistics */}
            <div className="bg-white dark:bg-dark-bg rounded-lg p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bike className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                Green Logistics
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Transporteur
              </p>
            </div>

            {/* Electric Vehicles */}
            <div className="bg-white dark:bg-dark-bg rounded-lg p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Battery className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                Electric Vehicles
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Constructeur
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Partenaires */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-white dark:from-dark-bg-secondary dark:to-dark-bg">
        <div className="container-ksl">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12 text-gray-900 dark:text-white">
            Nos Partenaires
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: "Eva",
                logo: "/Eva.jpeg", 
                description: "Plateforme qui vise à permettre aux commerçants locaux de prospérer dans un paysage omnicanal en constante évolution en offrant une expérience de livraison inégalée",
                website: "https://goeva.com/"
              },
              {
                name: "MTN",
                logo: "/MTN.png",
                description: "Opérateur télécoms leader en Afrique",
                website: "https://www.mtn.ci/"
              },
              {
                name: "MoMo Market",
                logo: "/momo.jpeg",
                description: "Plateforme e-commerce de MTN pour le commerce digital en Afrique",
                website: "https://market.momo.africa/"
              }
            ].map((partner, i) => (
              <a 
                key={i} 
                href={partner.website}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white dark:bg-dark-bg-secondary rounded-xl shadow-lg hover:shadow-xl p-8 flex flex-col items-center border border-gray-100 dark:border-gray-700 transition-all duration-300 transform hover:-translate-y-2 block"
              >
                <div className="w-24 h-24 rounded-lg bg-white dark:bg-gray-800 flex items-center justify-center mb-6 shadow-md border border-gray-200 dark:border-gray-600">
                  <img 
                    src={partner.logo} 
                    alt={`Logo ${partner.name}`}
                    className="w-20 h-20 object-contain"
                  />
                </div>
                <h3 className="text-xl font-bold text-ksl-red mb-2">{partner.name}</h3>
                <p className="text-gray-600 dark:text-gray-300 text-center text-sm leading-relaxed">
                  {partner.description}
                </p>
                <div className="mt-4 text-ksl-red text-xs font-medium hover:underline">
                  Visiter le site →
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Modal d'erreur de configuration */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title={errorModalData.title}
        message={errorModalData.message}
        type={errorModalData.type}
      />
    </>
  );
} 